import {
  Button,
  MenuItem,
  Select,
  TextField,
  Grid,
  ImageListItemBar,
  IconButton,
  Box,
  Chip,
  Divider,
  Modal,
  Fade,
  Backdrop,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { query, orderBy, limit, getDocs, onSnapshot, updateDoc } from "firebase/firestore";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { propertyDoc, propertyCollection } from "../utils/firestorePaths";
import { updateMeta } from "../utils/auditFields";
import PropertyContext from "../contexts/PropertyContext";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import ImageUpload from "./ImageUpload";
import RoomTurnHistory from "./RoomTurnHistory";
import { useRoomCalculations } from "../hooks/useRoomCalculations";
import GenerateRoomStatusReport from "./reports/GenerateRoomStatusReport";
import { VENDORS_LIST } from "../constants/roomConstants";
import { getDifficultyLevel, getDifficultyColorFromScore } from "../utils/roomDifficulty";

function RoomsSingle({
  singleRoom,
  setSingleRoom,
  itemsByCategory,
  handleEdit,
  handleOpenModal,
  statusOptions,
  hKOptions,
  mTOptions,
  uniqueCategories,
  setSingleRoomTimeline,
  user,
}) {
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [currentRoomData, setCurrentRoomData] = useState(singleRoom);
  const [notesValue, setNotesValue] = useState(singleRoom?.unitNotes || "");
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const { selectedProperty, selectedClient, rooms, items } = useContext(PropertyContext);

  // Get cost calculation for this room
  const { getCost, getDifficulty } = useRoomCalculations(rooms, items);
  const roomCost = getCost(currentRoomData?.displayName || '');

  // Function to fetch images - defined before useEffect
  const fetchLatestImages = async () => {
    // Reference to your collection
    const imagesRef = propertyCollection(
      selectedClient,
      selectedProperty,
      "units",
      singleRoom.displayName,
      "images"
    );

    // Create a query against the collection, ordering by upload timestamp descending
    const q = query(imagesRef, orderBy("uploadedAt", "desc"), limit(10));

    try {
      const querySnapshot = await getDocs(q);
      const images = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Assuming you have a state to hold these images
      setImages(images); // Or process the array as needed
      setLoading(false);
    } catch (error) {
      console.error("Error fetching images: ", error);
      // Handle any errors, such as showing a message to the user
      setLoading(false);
    }
  };

  // Intercept browser back button and use our custom back navigation
  useEffect(() => {
    // Push a new history state when component mounts
    window.history.pushState({ roomSingle: true }, '');

    const handlePopState = (event) => {
      // Prevent default back navigation and use our back function
      setSingleRoom();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [setSingleRoom]);

  // Set up real-time subscription for room data auto-updates
  useEffect(() => {
    if (!singleRoom?.displayName || !selectedProperty) return;

    const unsubscribeRoom = onSnapshot(
      propertyDoc(selectedClient, selectedProperty, "units", singleRoom.displayName),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const updatedRoomData = { ...docSnapshot.data(), displayName: docSnapshot.id };
          setCurrentRoomData(updatedRoomData);
          setNotesValue(updatedRoomData.unitNotes || "");
        }
      },
      (error) => {
        console.error("Error listening to room updates:", error);
      }
    );

    return () => unsubscribeRoom();
  }, [singleRoom?.displayName, selectedProperty]);

  // useEffect hook for images
  useEffect(() => {
    if (singleRoom && selectedProperty) {
      fetchLatestImages();
    }
  }, [singleRoom, selectedProperty]);

  const openCarousel = (index) => {
    setCarouselIndex(index);
    setCarouselOpen(true);
  };

  const closeCarousel = () => setCarouselOpen(false);

  const goPrev = useCallback(() => {
    setCarouselIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setCarouselIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!carouselOpen) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") closeCarousel();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [carouselOpen, goPrev, goNext]);

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      await GenerateRoomStatusReport({
        room: currentRoomData,
        clientId: selectedClient,
        selectedProperty,
        items,
        user,
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleRoomNameContextMenu = (event) => {
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleClearNeededItems = async () => {
    handleContextMenuClose();
    if (!currentRoomData || !selectedProperty) return;

    // Collect all item fields with values > 0
    const clearFields = {};
    if (items) {
      items.forEach((item) => {
        if (parseInt(currentRoomData[item.itemID]) > 0) {
          clearFields[item.itemID] = 0;
        }
      });
    }
    // Also clear vendor fields
    VENDORS_LIST.forEach((vendor) => {
      if (parseInt(currentRoomData[vendor.itemID]) > 0) {
        clearFields[vendor.itemID] = 0;
      }
    });

    if (Object.keys(clearFields).length === 0) return;

    try {
      await updateDoc(
        propertyDoc(selectedClient, selectedProperty, "units", currentRoomData.displayName),
        { ...clearFields, ...updateMeta() }
      );
    } catch (error) {
      console.error("Error clearing needed items:", error);
    }
  };

  const handleOpenCopyModal = () => {
    handleContextMenuClose();
    setCopyModalOpen(true);
  };

  const handleCloseCopyModal = () => {
    if (copyingFrom) return; // don't close mid-write
    setCopyModalOpen(false);
  };

  const handleCopyItemsFromRoom = async (sourceRoom) => {
    if (!currentRoomData || !selectedProperty || !sourceRoom) return;

    // Build the full set of item + vendor fields, copying the source room's
    // values (defaulting to 0 so fields the source doesn't need overwrite any
    // existing values on the target room).
    const copyFields = {};
    if (items) {
      items.forEach((item) => {
        copyFields[item.itemID] = parseInt(sourceRoom[item.itemID]) || 0;
      });
    }
    VENDORS_LIST.forEach((vendor) => {
      copyFields[vendor.itemID] = parseInt(sourceRoom[vendor.itemID]) || 0;
    });

    setCopyingFrom(sourceRoom.displayName);
    try {
      await updateDoc(
        propertyDoc(selectedClient, selectedProperty, "units", currentRoomData.displayName),
        { ...copyFields, ...updateMeta() }
      );
      setCopyModalOpen(false);
    } catch (error) {
      console.error("Error copying needed items:", error);
    } finally {
      setCopyingFrom(null);
    }
  };

  // Safety check - if singleRoom is not provided, don't render
  if (!singleRoom) {
    return null;
  }

  // Helper to get status options - includes Facilities only if room already has that status
  const getStatusOptions = (currentStatus) => {
    const baseOptions = [...statusOptions];
    const statusLower = (currentStatus || '').toLowerCase();

    if (statusLower === 'facilities' && !baseOptions.some(opt => opt.toLowerCase() === 'facilities')) {
      baseOptions.push('Facilities');
    }

    return baseOptions;
  };

  const renderCategoryButton = (category, itemsByCategory) => {
    const needsCategory =
      itemsByCategory?.[category] &&
      currentRoomData &&
      itemsByCategory[category]?.some(
        (item) => parseInt(currentRoomData[item?.itemID]) > 0
      );

    return (
      <>
        <Grid item xs={3}>
          <Typography>{category}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Button
            sx={{ width: "95%" }}
            variant="contained"
            color={needsCategory ? "error" : "success"}
            onClick={() => handleOpenModal(currentRoomData, category)}
          >
            {needsCategory ? "Yes" : "No"}
          </Button>
        </Grid>
      </>
    );
  };

  return (
    <div>
      <Card sx={{ minWidth: 275 }}>
        <CardHeader
          style={{ background: "#282828" }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h6"
                color="white"
                onContextMenu={handleRoomNameContextMenu}
                sx={{ cursor: 'context-menu', userSelect: 'none' }}
              >
                {currentRoomData?.displayName}
              </Typography>
              <Select
                value={currentRoomData?.status || ''}
                onChange={(event) => {
                  handleEdit(
                    currentRoomData.displayName,
                    "headerdata",
                    { status: event.target.value }
                  );
                }}
                size="small"
                sx={{
                  width: "150px",
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  }
                }}
              >
                {getStatusOptions(currentRoomData?.status)?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )) || []}
              </Select>
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", pr: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={generatingReport}
                onClick={handleGenerateReport}
                startIcon={<PictureAsPdfIcon />}
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.5)" }}
              >
                {generatingReport ? "Generating..." : "Status Report"}
              </Button>
              <Button variant="contained" onClick={() => setSingleRoom()}>
                Back
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography>Housekeeping</Typography>
            </Grid>
            <Grid item xs={3}>
              <Select
                labelId="housekeeping-select-label"
                id="housekeeping-select"
                value={currentRoomData?.selectedHousekeepingRequirements || ''}
                onChange={(event) => {
                  handleEdit(
                    currentRoomData.displayName,
                    "headerdata",
                    { selectedHousekeepingRequirements: event.target.value }
                  );
                }}
                size="small"
                sx={{ width: "100%" }}
              >
                {hKOptions?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )) || []}
              </Select>
            </Grid>
            <Grid item xs={3}>
              <Typography>Maintenance</Typography>
            </Grid>
            <Grid item xs={3}>
              <Select
                labelId="maintenance-select-label"
                id="maintenance-select"
                value={currentRoomData?.selectedMaintenanceRequirements || ''}
                onChange={(event) => {
                  handleEdit(
                    currentRoomData.displayName,
                    "headerdata",
                    { selectedMaintenanceRequirements: event.target.value }
                  );
                }}
                size="small"
                sx={{ width: "100%" }}
              >
                {mTOptions?.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                )) || []}
              </Select>
            </Grid>

            {uniqueCategories?.map((category) =>
              renderCategoryButton(category, itemsByCategory)
            ) || []}

            {/* Room Notes */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Notes
              </Typography>
              <TextField
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                onBlur={() => {
                  if (notesValue !== (currentRoomData?.unitNotes || "")) {
                    handleEdit(currentRoomData.displayName, "headerdata", {
                      unitNotes: notesValue,
                    });
                  }
                }}
                placeholder="Add notes for this room..."
                multiline
                minRows={2}
                fullWidth
                size="small"
              />
            </Grid>

            {/* Total Cost Display */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  Total Cost to Bring Room Up:
                </Typography>
                <Chip
                  label={`$${roomCost.toFixed(2)}`}
                  color="primary"
                  size="large"
                  sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Turn History — dispatched vs. completed, by date and department */}
      <RoomTurnHistory
        roomDisplayName={currentRoomData?.displayName || singleRoom.displayName}
      />

      <Card sx={{ minWidth: 275, marginTop: "50px" }}>
        <CardHeader
          style={{ background: "#282828" }}
          title="Latest Images"
          action={
            <Button
              variant="contained"
              onClick={() => setSingleRoomTimeline(true)}
            >
              View All
            </Button>
          }
        />
        <CardContent>
          <ImageList cols={5}>
            {images?.map((item, index) => (
              <ImageListItem key={item.id} sx={{ cursor: "pointer" }}>
                <img
                  srcSet={`${item.url}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
                  src={`${item.url}?w=164&h=164&fit=crop&auto=format`}
                  alt={item.name}
                  loading="lazy"
                  onClick={() => openCarousel(index)}
                  style={{ display: "block" }}
                />
                <ImageListItemBar
                  title={item.name}
                  actionIcon={
                    <IconButton
                      sx={{ color: "rgba(255, 255, 255, 0.54)" }}
                      aria-label={`info about ${item.name}`}
                      target="_blank"
                      href={item.url}
                      download={true}
                    >
                      <DownloadForOfflineIcon />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>

          {/* Carousel Modal */}
          <Modal
            open={carouselOpen}
            onClose={closeCarousel}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(0,0,0,0.9)" } } }}
          >
            <Fade in={carouselOpen}>
              <Box
                sx={{
                  position: "fixed",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  outline: "none",
                }}
              >
                {/* Close button */}
                <IconButton
                  onClick={closeCarousel}
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    zIndex: 10,
                  }}
                >
                  <CloseIcon />
                </IconButton>

                {/* Image counter */}
                <Box sx={{ color: "rgba(255,255,255,0.7)", mb: 2, fontSize: "0.875rem" }}>
                  {carouselIndex + 1} / {images.length}
                </Box>

                {/* Main carousel row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", px: 4, maxWidth: 1400 }}>
                  {/* Prev button */}
                  <IconButton
                    onClick={goPrev}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                      flexShrink: 0,
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>

                  {/* Prev thumbnail */}
                  {images.length > 1 && (
                    <Box
                      onClick={goPrev}
                      sx={{
                        width: 120,
                        height: 90,
                        flexShrink: 0,
                        cursor: "pointer",
                        opacity: 0.5,
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 0.8 },
                        overflow: "hidden",
                        borderRadius: 1,
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      <img
                        src={images[(carouselIndex - 1 + images.length) % images.length]?.url}
                        alt="previous"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </Box>
                  )}

                  {/* Main image */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      maxHeight: "70vh",
                    }}
                  >
                    <img
                      src={images[carouselIndex]?.url}
                      alt={images[carouselIndex]?.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        objectFit: "contain",
                        borderRadius: 8,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                      }}
                    />
                  </Box>

                  {/* Next thumbnail */}
                  {images.length > 1 && (
                    <Box
                      onClick={goNext}
                      sx={{
                        width: 120,
                        height: 90,
                        flexShrink: 0,
                        cursor: "pointer",
                        opacity: 0.5,
                        transition: "opacity 0.2s",
                        "&:hover": { opacity: 0.8 },
                        overflow: "hidden",
                        borderRadius: 1,
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      <img
                        src={images[(carouselIndex + 1) % images.length]?.url}
                        alt="next"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    </Box>
                  )}

                  {/* Next button */}
                  <IconButton
                    onClick={goNext}
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                      flexShrink: 0,
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>

                {/* Image name */}
                {images[carouselIndex]?.name && (
                  <Box sx={{ color: "rgba(255,255,255,0.7)", mt: 2, fontSize: "0.875rem" }}>
                    {images[carouselIndex].name}
                  </Box>
                )}
              </Box>
            </Fade>
          </Modal>
          <ImageUpload room={singleRoom} />
        </CardContent>
      </Card>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleClearNeededItems}>
          <ListItemIcon>
            <ClearAllIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear Needed Items</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenCopyModal}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Items From Room</ListItemText>
        </MenuItem>
      </Menu>

      <Modal
        open={copyModalOpen}
        onClose={handleCloseCopyModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300 } }}
      >
        <Fade in={copyModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: 480 },
              maxHeight: "80vh",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              display: "flex",
              flexDirection: "column",
              outline: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="h6">
                Copy Needed Items into {currentRoomData?.displayName}
              </Typography>
              <IconButton onClick={handleCloseCopyModal} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 2 }}>
              Select a room to copy its needed items and vendors from. This will
              overwrite the current needed items on this room.
            </Typography>

            <Box sx={{ overflowY: "auto", px: 2, py: 2 }}>
              {rooms
                ?.filter((room) => room.displayName !== currentRoomData?.displayName)
                .sort((a, b) => getDifficulty(b.displayName) - getDifficulty(a.displayName))
                .map((room) => {
                  const difficulty = getDifficulty(room.displayName);
                  const level = getDifficultyLevel(difficulty);
                  const color = getDifficultyColorFromScore(difficulty);
                  const isBusy = copyingFrom === room.displayName;
                  return (
                    <Box
                      key={room.displayName}
                      onClick={() => !copyingFrom && handleCopyItemsFromRoom(room)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.25,
                        mb: 1,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        cursor: copyingFrom ? "default" : "pointer",
                        opacity: copyingFrom && !isBusy ? 0.5 : 1,
                        transition: "background-color 0.15s",
                        "&:hover": {
                          bgcolor: copyingFrom ? "transparent" : "action.hover",
                        },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {room.displayName}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isBusy && (
                          <Typography variant="caption" color="text.secondary">
                            Copying…
                          </Typography>
                        )}
                        <Chip
                          label={`${level} (${difficulty})`}
                          size="small"
                          sx={{
                            bgcolor: color,
                            color: "#fff",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}

export default RoomsSingle;
